import React, {useEffect, useMemo, useState} from "react";
import {Button} from "react-bootstrap";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import {toastError, toastSuccess} from "../../../../helpers/Toast";

const SHOW_TEST_DATA_TOOLS = false;

const responseMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.message) return data.message;
  return fallback;
};

const EmployeeAssignmentTools = ({onChanged}) => {
  const axiosPrivate = useAxiosPrivate();
  const [busy, setBusy] = useState(false);
  const [mockAction, setMockAction] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const mockBusy = mockAction !== null;
  const anyBusy = busy || mockBusy;

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await axiosPrivate.get("/departments");
        const values = Array.isArray(response.data) ? response.data : [];
        setDepartments(values.slice().sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "pl")));
      } catch (error) {
        toastError(responseMessage(error, "Nie udało się pobrać listy działów."));
      } finally {
        setDepartmentsLoading(false);
      }
    };

    loadDepartments();
  }, [axiosPrivate]);

  const selectedDepartmentNames = useMemo(() => {
    const selected = new Set(selectedDepartmentIds.map(Number));
    return departments
      .filter(department => selected.has(Number(department.id)))
      .map(department => department.name)
      .filter(Boolean);
  }, [departments, selectedDepartmentIds]);

  const toggleDepartment = id => {
    const numericId = Number(id);
    setSelectedDepartmentIds(previous => previous.includes(numericId)
      ? previous.filter(value => value !== numericId)
      : [...previous, numericId]);
  };

  const distribute = async () => {
    const selectedDescription = selectedDepartmentNames.length
      ? `Każdy zatrudniony pracownik zostanie przypisany do wszystkich zaznaczonych działów: ${selectedDepartmentNames.join(", ")}.`
      : "Nie zaznaczono działów, więc zatrudnieni pracownicy zostaną automatycznie i możliwie równomiernie rozdzieleni pomiędzy wszystkie działy.";

    const confirmed = window.confirm(
      `Statusy zatrudnienia nie będą zmieniane. Do rozdzielenia zostaną użyci wyłącznie pracownicy, którzy już mają etap „Zatrudnienie”. ${selectedDescription} Na stanowiska kierownicze automat wybierze wyłącznie osoby zatrudnione, które nie są uczniami ani studentami i mają co najmniej 30 lat. Najpierw zostaną obsadzone stanowiska kierownicze i funkcyjne, a dopiero potem pozostałe stanowiska. Operacja nie zostanie wykonana ponownie, jeśli istnieją już przypisania.`
    );
    if (!confirmed) return;

    setBusy(true);
    try {
      const response = await axiosPrivate.post("/employees/assignments/initialize", {
        departmentIds: selectedDepartmentIds
      });
      toastSuccess(response.data || "Przypisano zatrudnionych pracowników do działów, stanowisk i ról oraz ustawiono kierowników działów.");
      await onChanged?.();
    } catch (error) {
      toastError(responseMessage(error, "Nie udało się przygotować przypisań pracowników."));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Usunąć wszystkim pracownikom przypisane działy i stanowiska? Ta operacja zmieni dane wszystkich pracowników.")) return;
    setBusy(true);
    try {
      const response = await axiosPrivate.delete("/employees/assignments");
      toastSuccess(response.data || "Przypisania działów i stanowisk zostały usunięte.");
      await onChanged?.();
    } catch (error) {
      toastError(responseMessage(error, "Nie udało się usunąć przypisań działów i stanowisk."));
    } finally {
      setBusy(false);
    }
  };

  const loadMockData = async () => {
    const confirmed = window.confirm(
      "Uruchomić employeesMock.sql, usersMock.sql i timeCardsMock.sql? Skrypty dodają testowych pracowników, użytkowników, role użytkowników i karty RCP. Istniejące rekordy objęte konfliktami nie będą duplikowane."
    );
    if (!confirmed) return;

    setMockAction("load");
    try {
      const response = await axiosPrivate.post("/test-data/mock");
      const result = response.data || {};
      const employeesAdded = Number(result.employeesAdded || 0);
      const usersAdded = Number(result.usersAdded || 0);
      const userRoleAssignmentsAdded = Number(result.userRoleAssignmentsAdded || 0);
      const timeCardsAdded = Number(result.timeCardsAdded || 0);
      const added = employeesAdded + usersAdded + userRoleAssignmentsAdded + timeCardsAdded;

      if (added === 0) {
        toastSuccess("Skrypty MOCK wykonano poprawnie. Nie dodano nowych rekordów.");
      } else {
        toastSuccess(
          `Dane MOCK dodane: pracownicy ${employeesAdded}, użytkownicy ${usersAdded}, role użytkowników ${userRoleAssignmentsAdded}, karty RCP ${timeCardsAdded}.`
        );
      }
      await onChanged?.();
    } catch (error) {
      toastError(responseMessage(error, "Nie udało się dodać danych testowych z plików MOCK."));
    } finally {
      setMockAction(null);
    }
  };


  const deleteMockData = async () => {
    const confirmed = window.confirm(
      "Usunąć dane testowe utworzone przez employeesMock.sql, usersMock.sql i timeCardsMock.sql? Usunięte zostaną wyłącznie rekordy oznaczone w bazie test_flag=true. Powiązane przypisania organizacyjne tych pracowników również zostaną wyczyszczone."
    );
    if (!confirmed) return;

    setMockAction("delete");
    try {
      const response = await axiosPrivate.delete("/test-data/mock");
      const result = response.data || {};
      const employeesRemoved = Number(result.employeesRemoved || 0);
      const usersRemoved = Number(result.usersRemoved || 0);
      const userRoleAssignmentsRemoved = Number(result.userRoleAssignmentsRemoved || 0);
      const timeCardsRemoved = Number(result.timeCardsRemoved || 0);
      const removed = employeesRemoved + usersRemoved + userRoleAssignmentsRemoved + timeCardsRemoved;

      if (removed === 0) {
        toastSuccess("Nie znaleziono danych testowych MOCK do usunięcia.");
      } else {
        toastSuccess(
          `Dane MOCK usunięte: pracownicy ${employeesRemoved}, użytkownicy ${usersRemoved}, role użytkowników ${userRoleAssignmentsRemoved}, karty RCP ${timeCardsRemoved}.`
        );
      }
      await onChanged?.();
    } catch (error) {
      toastError(responseMessage(error, "Nie udało się usunąć danych testowych z plików MOCK."));
    } finally {
      setMockAction(null);
    }
  };

  if (!SHOW_TEST_DATA_TOOLS) return null;

  return (
    <div className="container-fluid px-4 pt-3">
      <div className="card shadow-sm bg-white rounded-2 px-3 py-2">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
          <div className="flex-grow-1">
            <strong>Przypisania organizacyjne pracowników</strong>
            <div className="small text-muted mb-2">
              Statusy zatrudnienia nie są zmieniane. Rozdzielani są wyłącznie pracownicy z etapem „Zatrudnienie”. Brak zaznaczonych działów oznacza automatyczne rozdzielenie ich pomiędzy wszystkie działy. Po zaznaczeniu działów każdy zatrudniony zostanie przypisany do każdego z nich. Stanowiska kierownicze otrzymują tylko osoby, które nie są uczniami ani studentami i mają co najmniej 30 lat.
            </div>

            <div className="border rounded-2 px-2 py-2 bg-light">
              <div className="small fw-semibold mb-1">Działy docelowe</div>
              {departmentsLoading ? (
                <div className="small text-muted">Ładowanie działów...</div>
              ) : departments.length ? (
                <div className="d-flex flex-wrap gap-x-3 gap-y-1">
                  {departments.map(department => (
                    <label key={department.id} className="form-check form-check-inline mb-1 me-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedDepartmentIds.includes(Number(department.id))}
                        disabled={anyBusy}
                        onChange={() => toggleDepartment(department.id)}
                      />
                      <span className="form-check-label">{department.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="small text-muted">Brak zdefiniowanych działów.</div>
              )}
              <div className="small text-muted mt-1">
                {selectedDepartmentIds.length === 0
                  ? "Tryb: automatyczne rozdzielenie po wszystkich działach."
                  : `Tryb: każdy zatrudniony trafi do ${selectedDepartmentIds.length} zaznaczonych działów.`}
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <Button size="sm" variant="outline-primary" disabled={anyBusy || departmentsLoading || departments.length === 0} onClick={distribute}>
              Rozdziel i przypisz pracowników
            </Button>
            <Button size="sm" variant="outline-danger" disabled={anyBusy} onClick={clear}>
              Usuń przypisania działów i stanowisk
            </Button>
          </div>
        </div>
      </div>

      {SHOW_TEST_DATA_TOOLS && (
        <div className="card shadow-sm bg-white rounded-2 px-3 py-2 mt-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="flex-grow-1">
              <strong>Dane testowe MOCK</strong>
              <div className="small text-muted">
                Dodawanie uruchamia kolejno employeesMock.sql, usersMock.sql i timeCardsMock.sql i oznacza utworzone rekordy test_flag=true. Zwykłe dane mają test_flag=false. Usuwanie działa wyłącznie na rekordach oznaczonych jako testowe i czyści także ich przypisania organizacyjne. Dane MOCK dodane starszą wersją mechanizmu można oznaczyć przez ponowne użycie opcji dodawania; rekordy nie zostaną zduplikowane.
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button size="sm" variant="outline-primary" disabled={anyBusy} onClick={loadMockData}>
                {mockAction === "load" ? "Dodawanie danych testowych..." : "Dodaj dane testowe MOCK"}
              </Button>
              <Button size="sm" variant="outline-danger" disabled={anyBusy} onClick={deleteMockData}>
                {mockAction === "delete" ? "Usuwanie danych testowych..." : "Usuń dane testowe MOCK"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeAssignmentTools;

